import {
  createWebHistory,
  createRouter,
  RouteLocationNormalized,
  NavigationGuardNext,
} from 'vue-router';
import { routes } from './routes';
import useAuthStore from '@/stores/auth';

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Routes that should redirect to the previous navigation after the user is authenticated
const redirectRoutes = ['Login'];

router.beforeEach(navigationGuard);

/**
 * Navigation guard to handle authentication and redirection.
 * It goes through the following checks:
 * 1. If the user is authenticated and trying to access the login page, redirect to the previous persisted navigation.
 * 2. If the user is not authenticated and trying to access a route that requires authentication, redirect to the login page.
 * 3. If the user is authenticated, allow access to the requested route.
 *
 * @param to - The target route being navigated to.
 * @param _from - The current route being navigated from (not used here).
 * @param next - The function to call to resolve the navigation.
 */
async function navigationGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const { setPreviousNavigation, previousNavigation, isAuthenticated } =
    useAuthStore();

  const authenticated = await isAuthenticated();
  const isRedirectRoute = redirectRoutes.includes(to.name as string);

  if (authenticated && isRedirectRoute) {
    // @ts-expect-error this is not properly typed ( should be RouteLocationRaw), but for convenience i will ignore this for now
    return next(previousNavigation);
  }

  // Save the previous navigation state
  if (!isRedirectRoute) setPreviousNavigation(to);

  if (to.meta.requiresAuth && !authenticated) {
    return next({ name: 'Login' });
  }
  return next();
}

export default router;
